<script setup lang="ts">
import type { UnwrapNestedRefs } from 'vue'
import { TAILOR_DEMO_MAX_POSTING, TAILOR_DEMO_STEPS, type TailorDemo } from '~/composables/useTailorDemo'
import type { CVData } from '~/types'
import { addedSkills, diffTailoring, skillsReordered, type TailorChange, type TailorPreviewChange } from '~/utils/cvTailor'

/**
 * The CV just built, rewritten for the posting the visitor wants, in seconds.
 *
 * Shown as a card under the result and inside the panel that opens after the
 * free download. It proves the pack's main promise instead of describing it:
 * the score before and after, a timer that says how fast it was, and one
 * change in full (usually the profile) next to what the CV says now. The
 * other changes are already made and listed by name, locked: a click adds
 * one to the CV and the match rises (liveMatchScore), its text stays locked
 * until it is paid for. Once paid, the buyer saves it onto a free account and
 * the editor lists every change against the CV it was written from.
 *
 * Skills the posting wants that the CV does not mention are offered to tick:
 * each one ticked raises the score (confirmedScore) and goes into the tailored
 * PDF, with a line under a role if they say where they used it
 * (CommonSkillLines). Nothing is added that the visitor did not say is true. Below
 * TAILOR_LOW_MATCH the result is not celebrated: no "+4 %" badge, a plain
 * note that the CV does not show what the job asks for.
 */
const props = withDefaults(defineProps<{
  demo: UnwrapNestedRefs<TailorDemo>
  cvData: CVData
  /** The pack can be bought; false for someone whose plan already covers it. */
  canBuyPack: boolean
  /** A checkout is opening; disables the button. */
  isBuying?: boolean
  /** Where it is shown, for the journey event. */
  where?: 'card' | 'after_download'
  /** Inside a panel that has its own heading: no card frame or title. */
  bare?: boolean
  /** This tailoring can be bought on its own as one clean PDF (`tailored-pdf`). */
  canBuyTailored?: boolean
  /** It can also be bought as the application kit: with the cover letter and the interview prep (`application-kit`). */
  canBuyKit?: boolean
  /** It has been: the file went down, the card offers it again. */
  tailoredPaid?: boolean
  /** The purchase's own page, for the buyer who wants it later or elsewhere. */
  tailoredDownloadUrl?: string | null
  /** The paid tailored PDF's token: the interview prep for the same posting is offered on it. */
  tailoredToken?: string | null
  /** The paid PDF is being printed. */
  isExporting?: boolean
  /**
   * The posting in the field is the one they gave while the CV was built (or
   * the day-0 e-mail's link brought back): the CV is not tailored to it, so
   * the card says it can be, and what that costs.
   */
  offerPosting?: boolean
  /** The result can also be read larger: a button asks the host for its wide dialog. */
  expandable?: boolean
  /** Over the title, e.g. which step of the result this is ("Step 2 · Free preview"). */
  eyebrow?: string
  /** The posting field open from the start, no button in front of it: the next step is one paste away. */
  startOpen?: boolean
  /**
   * The result ends on one choice of three, the pack first: everything for
   * every posting, this CV tailored as one PDF, or the general CV for free.
   */
  tiers?: boolean
}>(), {
  isBuying: false,
  where: 'card',
  bare: false,
  canBuyTailored: false,
  canBuyKit: false,
  tailoredPaid: false,
  tailoredDownloadUrl: null,
  tailoredToken: null,
  isExporting: false,
  offerPosting: false,
  expandable: false,
  eyebrow: undefined,
  startOpen: false,
  tiers: false,
})

const emit = defineEmits<{
  /** Buy the pack; the host opens its checkout. */
  buy: []
  /** Buy this tailoring as one clean PDF. */
  'buy-tailored': []
  /** Buy it as the application kit, with the cover letter and the interview prep. */
  'buy-kit': []
  /** Download the paid tailored PDF again. */
  'download-tailored': []
  /** Premium already has it: open the editor, which tailors to the parked posting. */
  edit: []
  /** Show the result in the host's wide dialog. */
  expand: []
  /** The general CV, free, from the choice of three (`tiers`). */
  'download-free': []
}>()

const { t, n } = useI18n()
const { isAuthenticated } = useAuthUser()
const { claimAndOpen } = useGuestClaim()
const { tailoredPdfPrice, applicationKitPrice } = usePremiumPlans()
/** Less what was paid for this CV before, as the checkout charges it (usePackCredit). */
const { packPrice: jobPackPrice, creditNote } = usePackCredit()
/** Three postings bought one by one, next to the pack's price. */
const { prices, format } = usePricing().pricing.value
const threeTailored = format(prices.tailoredPdf * 3)
/** Three applications bought one by one, each with its tailored CV and interview prep: what the pack is priced against. */
const threeApplications = format((prices.tailoredPdf + prices.interviewPrep) * 3)

const preview = computed(() => props.demo.preview)
/**
 * The card starts small, a title, a line and a button, so the column under
 * the CV stays short; the field opens on the click. Inside a panel of its own
 * (`bare`) or with a posting already in it, it is open from the start.
 */
const opened = ref(false)
const showForm = computed(() => props.bare || props.startOpen || opened.value || !!props.demo.posting.trim() || props.demo.limitReached)

function openForm() {
  opened.value = true
  // The field takes the button's place, so it is in view; a scroll on the way there is not cut short.
  nextTick(() => document.getElementById(`tailor-posting-${props.where}`)?.focus({ preventScroll: true }))
}
/** The match with the edits and the skills ticked so far. */
const after = computed(() => props.demo.liveScore)
const gain = computed(() => (preview.value ? after.value - preview.value.matchScore : 0))
/** What the posting asks for and wording cannot add, without the ones offered for ticking. */
const missing = computed(() => {
  const offered = new Set((preview.value?.confirmSkills ?? []).map(skill => skill.toLowerCase()))
  return (preview.value?.missingSkills ?? []).filter(skill => !offered.has(skill.toLowerCase()))
})
const scoreNote = computed(() => {
  if (props.demo.lowMatch) return t(preview.value?.confirmSkills.length ? 'landing.builder.jobMatch.scoreLow' : 'landing.builder.jobMatch.scoreLowNone')
  if (gain.value <= 0) return t('landing.builder.jobMatch.scoreSame')
  return t(props.demo.confirmed.length ? 'landing.builder.jobMatch.scoreConfirmed' : 'landing.builder.jobMatch.scoreAfter')
})
const changeCount = computed(() => (preview.value ? preview.value.locked.length + (preview.value.sample ? 1 : 0) : 0))
/** What one change adds to the match, as liveMatchScore counts it: its equal share of the edits' gain. */
const changeShare = computed(() => (preview.value && changeCount.value ? Math.round((preview.value.matchScoreAfter - preview.value.matchScore) / changeCount.value) : 0))
/** The same for one skill ticked as the visitor's own (confirmedScore). */
const skillShare = computed(() => (preview.value?.confirmSkills.length
  ? Math.round((preview.value.matchScoreConfirmed - preview.value.matchScoreAfter) / preview.value.confirmSkills.length)
  : 0))

/** The hover line on a locked change: what the click does, with its share of the match. */
function addTip(index: number): string {
  if (props.demo.added.includes(index)) return t('landing.builder.jobMatch.removeTip')
  return changeShare.value > 0 ? t('landing.builder.jobMatch.addTip', { gain: changeShare.value }) : t('landing.builder.jobMatch.addTipNoGain')
}

/*
 * The match stays in sight while the visitor scrolls down to add changes and
 * tick skills: once the big score box has scrolled away, a thin bar with the
 * same numbers sticks to the top (under the site's header on the page, at the
 * top of a dialog).
 */
const scoreBox = ref<HTMLElement | null>(null)
const scoreOut = ref(false)
watch(scoreBox, (el, _old, onCleanup) => {
  scoreOut.value = false
  if (!el || typeof IntersectionObserver === 'undefined') return
  const observer = new IntersectionObserver(([entry]) => {
    // Gone above, not yet reached below.
    scoreOut.value = !!entry && !entry.isIntersecting && entry.boundingClientRect.top < window.innerHeight / 2
  }, { rootMargin: props.bare ? '0px' : '-80px 0px 0px 0px' })
  observer.observe(el)
  onCleanup(() => observer.disconnect())
})

/** The same on a skill to tick. */
function confirmTip(skill: string): string {
  if (props.demo.confirmed.includes(skill)) return t('landing.builder.jobMatch.unconfirmTip')
  return skillShare.value > 0 ? t('landing.builder.jobMatch.confirmTip', { gain: skillShare.value }) : t('landing.builder.jobMatch.confirmTipNoGain')
}
const seconds = (value: number) => n(value, { maximumFractionDigits: 1, minimumFractionDigits: 1 })

const scoreClass = (score: number) => (score >= 75 ? '!text-accent-strong dark:!text-accent-soft' : score >= 50 ? '!text-amber-600 dark:!text-amber-400' : '!text-red-600 dark:!text-red-400')

/** "Profile", "Accountant · ABC s.r.o.", "Headline"… */
function changeLabel(change: TailorPreviewChange): string {
  if (change.part === 'experience') {
    // Older CVs name the role `title`, the chat builder `position`.
    const exp = props.cvData.experiences?.[change.experienceIndex ?? -1] as { position?: string; title?: string; company?: string } | undefined
    const name = [exp?.position || exp?.title, exp?.company].filter(Boolean).join(' · ')
    return name || t('landing.builder.jobMatch.parts.experience')
  }
  if (change.part === 'confirmSkills') return t('landing.builder.jobMatch.parts.confirmSkills', { n: change.count ?? 0 }, change.count ?? 0)
  return t(`landing.builder.jobMatch.parts.${change.part}`)
}

/*
 * Paid: what was bought is the edits, so they are shown whole, each next to
 * what the CV said before, instead of the locked list. They are read from the
 * paid CV itself (/api/guest-export/<token>), the one the PDF is printed from.
 */
const paidCv = ref<CVData | null>(null)
watch(() => (props.tailoredPaid ? props.tailoredToken : null), async (token) => {
  paidCv.value = null
  if (!token) return
  try {
    const response = await $fetch<{ cvData: CVData | null }>(`/api/guest-export/${token}`)
    paidCv.value = response.cvData
  } catch (err) {
    console.error('Could not load the tailored CV:', err)
  }
}, { immediate: true })

const paidChanges = computed(() => (paidCv.value ? diffTailoring(props.cvData, paidCv.value) : []))

function paidLabel(change: TailorChange): string {
  if (change.field === 'experience') return changeLabel({ part: 'experience', experienceIndex: change.index })
  if (change.field === 'technicalSkills') return t('landing.builder.tailored.skills')
  return t(`landing.builder.jobMatch.parts.${change.field}`)
}

/** The skills change in words: what was added, and whether the rest moved. */
function skillsText(change: Extract<TailorChange, { field: 'technicalSkills' }>): string {
  const added = addedSkills(change)
  return [
    added.length ? t('landing.builder.tailored.skillsAdded', { skills: added.join(', ') }) : '',
    skillsReordered(change) ? t('landing.builder.tailored.skillsOrdered') : '',
  ].filter(Boolean).join(' ')
}

/*
 * "Keep it on my account": the paid CV becomes a CV there (the same claim as
 * /download/<token>), with one more clean download, and the editor opens on it.
 * Signed out, a free account comes first.
 */
const showSignup = ref(false)
const isSaving = ref(false)
const saveError = ref<string | null>(null)

async function saveTailored() {
  if (!props.tailoredToken || isSaving.value) return
  saveError.value = null
  if (!isAuthenticated.value) {
    showSignup.value = true
    return
  }
  isSaving.value = true
  const opened = await claimAndOpen(props.tailoredToken)
  isSaving.value = false
  if (!opened) saveError.value = t('landing.download.edit.error')
}

async function onSignedIn() {
  showSignup.value = false
  await saveTailored()
}

/** Opens the posting field and focuses it, as the card's button does (ResultOffers `openTailor`). */
defineExpose({ openForm })
</script>

<template>
  <div :class="bare ? '' : 'card'">
    <div v-if="!bare" class="mb-1 flex items-start justify-between gap-4">
      <div>
        <div v-if="eyebrow" class="mb-1 text-xs font-semibold uppercase tracking-wide text-accent-strong dark:text-accent-soft">{{ eyebrow }}</div>
        <div class="font-title text-xl font-bold text-ink dark:text-white">{{ t('landing.builder.jobMatch.title') }}</div>
      </div>
      <i class="bi bi-magic shrink-0 text-4xl leading-none text-lilac" aria-hidden="true" />
    </div>

    <!-- 1. What it does, and the button that opens the field -->
    <template v-if="!preview && !demo.isRunning && !showForm">
      <p class="!mb-4 text-base">{{ t('landing.builder.jobMatch.text') }}</p>
      <SofaxBtn variant="dark" block :label="t('landing.builder.jobMatch.open')" @click="openForm" />
    </template>

    <!-- 1. The posting -->
    <form v-else-if="!preview && !demo.isRunning" novalidate @submit.prevent="demo.run(where)">
      <!-- Their posting, not yet used: what tailoring to it gives them and costs -->
      <div v-if="offerPosting" class="!mb-4">
        <p class="!mb-3 text-base">{{ t('landing.builder.jobMatch.offer.text') }}</p>
        <ul class="space-y-1.5 !text-sm">
          <li class="flex items-baseline justify-between gap-3">
            <span class="text-body dark:text-gray-300">{{ t('landing.builder.jobMatch.offer.single') }}</span>
            <span class="shrink-0 font-title text-base font-bold tabular-nums text-ink dark:text-white">{{ tailoredPdfPrice }}</span>
          </li>
          <li class="flex items-baseline justify-between gap-3">
            <span class="text-body dark:text-gray-300">{{ t('landing.builder.jobMatch.offer.pack') }}</span>
            <span class="shrink-0 font-title text-base font-bold tabular-nums text-ink dark:text-white">{{ jobPackPrice }}</span>
          </li>
        </ul>
      </div>
      <p v-else-if="!bare" class="!mb-4 text-base">{{ t('landing.builder.jobMatch.text') }}</p>
      <div class="rounded-[0.625rem] border border-line bg-white transition-colors focus-within:border-accent dark:border-gray-700 dark:bg-gray-800">
        <label :for="`tailor-posting-${where}`" class="sr-only">{{ t('landing.builder.jobMatch.label') }}</label>
        <textarea
          :id="`tailor-posting-${where}`"
          :value="demo.posting"
          rows="5"
          :maxlength="TAILOR_DEMO_MAX_POSTING"
          :placeholder="t('landing.builder.jobMatch.placeholder')"
          class="block w-full resize-y rounded-[0.625rem] border-0 bg-transparent p-4 text-base leading-relaxed text-ink placeholder:text-muted focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-gray-500"
          @input="demo.setPosting(($event.target as HTMLTextAreaElement).value)"
        />
      </div>
      <CommonPostingUrlStatus :is-loading="demo.postingUrlLoading" :error="demo.postingUrlError" :loaded-from="demo.postingLoadedFrom" />
      <p v-if="demo.message" class="mt-3 !text-sm text-amber-700 dark:text-amber-400">{{ demo.message }}</p>

      <!-- Today's free run is used: say so plainly, and offer the pack that has no daily limit -->
      <SofaxNotice v-if="demo.limitReached" type="warning" class="mt-4">
        <div class="font-semibold">{{ t('landing.builder.jobMatch.limitTitle') }}</div>
        <p class="mt-1 !text-sm">{{ t('landing.builder.jobMatch.limit') }}</p>
        <SofaxBtn
          v-if="canBuyPack"
          variant="accent"
          block
          class="mt-3"
          :disabled="isBuying"
          :label="isBuying ? t('common.loading') : t('landing.builder.jobMatch.limitCta', { price: jobPackPrice })"
          @click="emit('buy')"
        />
      </SofaxNotice>
      <SofaxBtn
        v-else
        type="submit"
        variant="dark"
        block
        class="mt-4"
        :disabled="!demo.posting.trim() || demo.postingIsLink"
        :label="t('landing.builder.jobMatch.cta')"
      />
      <p class="mt-2 text-center !text-sm text-muted dark:text-gray-400">
        <i class="bi bi-stopwatch mr-1" aria-hidden="true" />{{ t(offerPosting ? 'landing.builder.jobMatch.offer.note' : 'landing.builder.jobMatch.speed') }}
      </p>
    </form>

    <!-- 2. The work, as it happens: what the AI is doing and a running clock -->
    <div v-else-if="demo.isRunning" class="py-2" aria-live="polite">
      <div class="mb-4 flex items-baseline justify-between gap-4">
        <div class="font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.jobMatch.working') }}</div>
        <div class="font-title text-2xl font-bold tabular-nums text-ink dark:text-white">{{ seconds(demo.elapsed) }}&nbsp;s</div>
      </div>
      <ul class="space-y-2.5">
        <li v-for="(id, index) in TAILOR_DEMO_STEPS" :key="id" class="flex items-center gap-3 text-base">
          <i
            v-if="index < demo.step"
            class="bi bi-check-circle-fill shrink-0 text-lg text-accent"
            aria-hidden="true"
          />
          <i
            v-else-if="index === demo.step"
            class="bi bi-arrow-repeat shrink-0 animate-spin text-lg text-ink dark:text-white"
            aria-hidden="true"
          />
          <i v-else class="bi bi-circle shrink-0 text-lg text-line dark:text-gray-600" aria-hidden="true" />
          <span :class="index <= demo.step ? 'text-ink dark:text-white' : 'text-muted dark:text-gray-500'">
            {{ t(`landing.builder.jobMatch.steps.${id}`) }}
          </span>
        </li>
      </ul>
    </div>

    <!-- 3. The result -->
    <template v-else-if="preview">
      <div class="mb-4 flex flex-wrap items-center gap-2 font-title text-base font-semibold text-accent-strong dark:text-accent-soft">
        <i class="bi bi-lightning-charge-fill" aria-hidden="true" />
        <span v-if="demo.tookSeconds !== null">
          {{ changeCount
            ? t('landing.builder.jobMatch.done', { n: changeCount, s: seconds(demo.tookSeconds) }, changeCount)
            : t('landing.builder.jobMatch.doneOnly', { s: seconds(demo.tookSeconds) }) }}
        </span>
        <button
          v-if="expandable && !tailoredPaid"
          type="button"
          class="action-pill ml-auto h-8 !text-xs"
          @click="emit('expand')"
        >
          <i class="bi bi-arrows-angle-expand" aria-hidden="true" />
          {{ t('landing.builder.jobMatch.expand') }}
        </button>
      </div>

      <!-- The problem first, in plain words: what the CV as it is leaves out of this posting -->
      <SofaxNotice v-if="preview.matchScore < 75" type="warning" class="mb-4">
        <div class="font-semibold">{{ t('landing.builder.jobMatch.problemTitle', { score: preview.matchScore }) }}</div>
        <p class="mt-1 !text-sm">{{ t('landing.builder.jobMatch.problemText') }}</p>
      </SofaxNotice>

      <!-- The match, kept in sight once the box below has scrolled away; no height of its own, so nothing jumps -->
      <div v-if="!tailoredPaid" class="sticky z-30 h-0" :class="bare ? 'top-0' : 'top-20'">
        <Transition name="score-bar">
          <div v-if="scoreOut" class="score-bar" aria-hidden="true">
            <span class="truncate !text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ t('landing.builder.jobMatch.scoreLabel') }}</span>
            <span class="ml-auto flex shrink-0 items-baseline gap-2 font-title font-bold tabular-nums">
              <span class="!text-base" :class="scoreClass(preview.matchScore)">{{ preview.matchScore }}&nbsp;%</span>
              <template v-if="gain > 0">
                <i class="bi bi-arrow-right !text-sm text-muted" />
                <span class="!text-xl transition-colors" :class="scoreClass(after)">{{ after }}&nbsp;%</span>
                <span v-if="!demo.lowMatch" class="self-center rounded-full bg-accent-strong px-2 py-0.5 !text-xs !text-white">+{{ gain }}&nbsp;%</span>
              </template>
            </span>
          </div>
        </Transition>
      </div>

      <!-- Before and after, as two rows of the same shape -->
      <div ref="scoreBox" class="mb-5 rounded-[0.625rem] bg-surface p-4 dark:bg-gray-900">
        <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
          {{ t('landing.builder.jobMatch.scoreLabel') }}
        </div>
        <div class="flex items-baseline gap-3 font-title font-bold tabular-nums" aria-live="polite">
          <span class="!text-3xl" :class="scoreClass(preview.matchScore)">{{ preview.matchScore }}&nbsp;%</span>
          <template v-if="gain > 0">
            <i class="bi bi-arrow-right !text-2xl text-muted" aria-hidden="true" />
            <span class="!text-5xl transition-colors" :class="scoreClass(after)">{{ after }}&nbsp;%</span>
            <!-- A low match is not a success to celebrate: no badge, the note below says it plainly -->
            <span v-if="!demo.lowMatch" class="ml-auto self-center rounded-full bg-accent-strong px-2.5 py-1 !text-sm !text-white">+{{ gain }}&nbsp;%</span>
          </template>
        </div>
        <!-- One bar: what the CV covers now, and what the changes and ticked skills add -->
        <div class="mt-3 flex h-2 overflow-hidden rounded-full bg-line dark:bg-gray-700" aria-hidden="true">
          <div class="bg-ink/40 dark:bg-white/40" :style="{ width: `${preview.matchScore}%` }" />
          <div class="transition-[width] duration-300" :class="demo.lowMatch ? 'bg-ink/25 dark:bg-white/25' : 'bg-accent'" :style="{ width: `${gain}%` }" />
        </div>
        <p class="mt-2 !text-sm" :class="demo.lowMatch ? 'font-semibold text-ink dark:text-white' : 'text-body dark:text-gray-300'">
          {{ scoreNote }}
        </p>
      </div>

      <!-- Skills the posting wants that the CV does not mention: ticked, they count and go into the CV -->
      <div v-if="preview.confirmSkills.length && !tailoredPaid" class="mb-5">
        <div class="mb-1 font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.jobMatch.confirmTitle') }}</div>
        <p class="!mb-3 !text-sm text-muted dark:text-gray-400">{{ t('landing.builder.jobMatch.confirmHint') }}</p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="skill in preview.confirmSkills"
            :key="skill"
            type="button"
            :aria-pressed="demo.confirmed.includes(skill)"
            class="group relative inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold transition-colors"
            :class="demo.confirmed.includes(skill)
              ? 'border-accent bg-accent text-white'
              : 'border-line bg-white text-ink hover:border-accent dark:border-gray-600 dark:bg-gray-800 dark:text-white'"
            @click="demo.toggleConfirmed(skill)"
          >
            <i class="bi text-xs" :class="demo.confirmed.includes(skill) ? 'bi-check-lg' : 'bi-plus-lg'" aria-hidden="true" />
            {{ skill }}
            <span class="hover-tip" aria-hidden="true">{{ confirmTip(skill) }}</span>
          </button>
        </div>
        <CommonSkillLines
          :lines="demo.skillLines"
          :skills="preview.confirmSkills.filter(skill => demo.confirmed.includes(skill))"
          :experiences="cvData.experiences"
          :posting="demo.posting"
          @update:lines="demo.setSkillLines"
        />
      </div>

      <!-- The proof: one change in full -->
      <div v-if="preview.sample && !tailoredPaid" class="mb-5">
        <div class="mb-2 font-title text-base font-semibold text-ink dark:text-white">
          {{ t('landing.builder.jobMatch.sampleTitle', { part: changeLabel(preview.sample) }) }}
        </div>
        <div v-if="preview.sample.before" class="rounded-t-[0.625rem] border border-b-0 border-line p-3 dark:border-gray-700">
          <div class="mb-1 text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ t('landing.builder.jobMatch.before') }}</div>
          <p class="line-clamp-4 whitespace-pre-line !text-sm text-muted dark:text-gray-400">{{ preview.sample.before }}</p>
        </div>
        <div
          class="border-2 border-accent bg-accent/5 p-3 dark:bg-accent/10"
          :class="preview.sample.before ? 'rounded-b-[0.625rem]' : 'rounded-[0.625rem]'"
        >
          <div class="mb-1 text-xs font-semibold uppercase tracking-wide text-accent-strong dark:text-accent-soft">{{ t('landing.builder.jobMatch.after') }}</div>
          <p class="whitespace-pre-line !text-sm text-ink dark:text-white">{{ preview.sample.after }}</p>
        </div>
      </div>

      <!--
        Everything else, already rewritten. On the free preview each one is
        added with a click: it counts in the match above at once, its text stays
        locked until payment (and the PDF bought has every one of them).
      -->
      <div v-if="preview.locked.length && !tailoredPaid" class="mb-5">
        <div class="mb-2 flex items-baseline justify-between gap-3">
          <div class="font-title text-base font-semibold text-ink dark:text-white">
            {{ t('landing.builder.jobMatch.lockedTitle', { n: preview.locked.length }, preview.locked.length) }}
          </div>
          <button
            v-if="preview.limited && demo.added.length < preview.locked.length"
            type="button"
            class="link shrink-0 !text-sm"
            @click="demo.addAll()"
          >
            {{ t('landing.builder.jobMatch.addAll') }}
          </button>
        </div>
        <p v-if="preview.limited" class="!mb-3 !text-sm text-muted dark:text-gray-400">{{ t('landing.builder.jobMatch.lockedHint') }}</p>
        <ul class="space-y-2">
          <li v-for="(change, index) in preview.locked" :key="index">
            <component
              :is="preview.limited ? 'button' : 'div'"
              :type="preview.limited ? 'button' : undefined"
              :aria-pressed="preview.limited ? demo.added.includes(index) : undefined"
              class="group relative block w-full rounded-[0.625rem] border p-3 text-left transition-colors"
              :class="preview.limited && demo.added.includes(index)
                ? 'border-accent bg-accent/5 dark:bg-accent/10'
                : preview.limited ? 'border-line hover:border-accent dark:border-gray-700' : 'border-line dark:border-gray-700'"
              @click="preview.limited && demo.toggleAdded(index)"
            >
              <span class="flex items-start gap-2">
                <i
                  class="bi mt-0.5 shrink-0"
                  :class="!preview.limited || demo.added.includes(index) ? 'bi-check-circle-fill text-accent' : 'bi-lock-fill text-muted'"
                  aria-hidden="true"
                />
                <span class="min-w-0 flex-1">
                  <span class="block font-title text-sm font-semibold text-ink dark:text-white">{{ changeLabel(change) }}</span>
                  <span v-if="change.reason" class="block text-sm text-body dark:text-gray-300">{{ change.reason }}</span>
                  <span v-if="change.after" class="mt-1 line-clamp-4 block whitespace-pre-line !text-sm text-ink dark:text-white">{{ change.after }}</span>
                  <!-- Locked text: the rewrite exists, its words are not sent until it is paid for -->
                  <span v-else-if="preview.limited && (change.part === 'experience' || change.part === 'title')" class="mt-2 block space-y-1.5" aria-hidden="true">
                    <span class="block h-2 w-11/12 rounded-full bg-line blur-[1.5px] dark:bg-gray-700" />
                    <span class="block h-2 w-4/5 rounded-full bg-line blur-[1.5px] dark:bg-gray-700" />
                    <span v-if="change.part === 'experience'" class="block h-2 w-2/3 rounded-full bg-line blur-[1.5px] dark:bg-gray-700" />
                  </span>
                  <span v-if="preview.limited && demo.added.includes(index)" class="mt-1.5 block !text-xs text-muted dark:text-gray-400">
                    <i class="bi bi-lock mr-1" aria-hidden="true" />{{ t('landing.builder.jobMatch.addedLocked') }}
                  </span>
                </span>
                <span
                  v-if="preview.limited"
                  class="shrink-0 rounded-full px-2.5 py-0.5 !text-xs font-semibold"
                  :class="demo.added.includes(index) ? 'bg-accent !text-white' : 'border border-line !text-ink dark:border-gray-600 dark:!text-white'"
                >
                  {{ demo.added.includes(index) ? `✓ ${t('landing.builder.jobMatch.addedLabel')}` : `+ ${t('landing.builder.jobMatch.add')}` }}
                </span>
              </span>
              <span v-if="preview.limited" class="hover-tip hover-tip-end" aria-hidden="true">{{ addTip(index) }}</span>
            </component>
          </li>
        </ul>
      </div>

      <!-- What wording cannot fix: said plainly, so the score above is believed -->
      <div v-if="missing.length || preview.hiddenMissing" class="mb-5">
        <div class="mb-1 font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.jobMatch.missing') }}</div>
        <p class="!mb-2 !text-sm text-muted dark:text-gray-400">{{ t('landing.builder.jobMatch.missingHint') }}</p>
        <ul class="space-y-1.5">
          <li v-for="skill in missing" :key="skill" class="flex items-start gap-2 text-sm text-body dark:text-gray-300">
            <i class="bi bi-x-circle-fill mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
            {{ skill }}
          </li>
        </ul>
        <!-- The rest of the list is locked: big enough to be seen, and honest about what unlocking does -->
        <div v-if="preview.hiddenMissing" class="mt-3 flex items-start gap-3 rounded-[0.625rem] bg-lilac/30 p-4 dark:bg-lilac/10">
          <i class="bi bi-lock-fill shrink-0 text-2xl leading-none text-ink dark:text-white" aria-hidden="true" />
          <div>
            <div class="font-title text-base font-bold text-ink dark:text-white">
              {{ t('landing.builder.jobMatch.moreMissingTitle', { n: preview.hiddenMissing }, preview.hiddenMissing) }}
            </div>
            <div v-if="preview.hiddenGain" class="mt-1 font-title text-base font-semibold !text-accent-strong dark:!text-accent-soft">
              {{ t('landing.builder.jobMatch.moreMissingGain', { gain: preview.hiddenGain }) }}
            </div>
            <p class="mt-1 !text-sm text-body dark:text-gray-300">{{ t('landing.builder.jobMatch.moreMissingText') }}</p>
          </div>
        </div>
      </div>

      <!-- Bought on its own: the tailored PDF went down, this is the repeat button,
           and the next step for the same posting is the interview -->
      <template v-if="tailoredPaid">
        <SofaxNotice type="success">
          <div class="font-semibold text-ink dark:text-white">{{ t('landing.builder.tailored.paidTitle') }}</div>
          <p class="mt-1 text-sm">{{ t('landing.builder.tailored.paidText') }}</p>
          <SofaxBtn
            variant="outline"
            block
            class="mt-3"
            :disabled="isExporting"
            :label="isExporting ? t('landing.builder.free.preparing') : t('landing.builder.tailored.again')"
            @click="emit('download-tailored')"
          />
          <a v-if="tailoredDownloadUrl" :href="tailoredDownloadUrl" class="link mt-3 inline-block !text-sm">
            {{ t('landing.builder.tailored.link') }}
          </a>
        </SofaxNotice>

        <!-- Next: the paid CV onto a free account, where every edit is listed in the editor to keep, change or undo -->
        <div v-if="tailoredToken" class="mt-4 rounded-[0.625rem] border-2 border-accent bg-accent/5 p-4 dark:bg-accent/10">
          <div class="font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tailored.saveTitle') }}</div>
          <p class="mt-1 !text-sm text-body dark:text-gray-300">{{ t('landing.builder.tailored.saveText') }}</p>
          <SofaxBtn
            variant="accent"
            block
            class="mt-3"
            :disabled="isSaving"
            :label="isSaving ? t('common.loading') : t(isAuthenticated ? 'landing.builder.tailored.saveCtaSignedIn' : 'landing.builder.tailored.saveCta')"
            @click="saveTailored"
          />
          <SofaxNotice v-if="saveError" type="error" class="mt-3">{{ saveError }}</SofaxNotice>
        </div>

        <!-- What was bought: every edit, whole, next to what the CV said before -->
        <div v-if="paidChanges.length" class="mt-6">
          <div class="mb-3 font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tailored.editsTitle') }}</div>
          <ul class="space-y-4">
            <li v-for="(change, index) in paidChanges" :key="index">
              <div class="mb-1 font-title text-sm font-semibold text-ink dark:text-white">{{ paidLabel(change) }}</div>
              <p v-if="change.field === 'technicalSkills'" class="!text-sm text-body dark:text-gray-300">{{ skillsText(change) }}</p>
              <template v-else>
                <div v-if="change.before" class="rounded-t-[0.625rem] border border-b-0 border-line p-3 dark:border-gray-700">
                  <div class="mb-1 text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ t('landing.builder.jobMatch.before') }}</div>
                  <p class="line-clamp-4 whitespace-pre-line !text-sm text-muted dark:text-gray-400">{{ change.before }}</p>
                </div>
                <div class="border-2 border-accent bg-accent/5 p-3 dark:bg-accent/10" :class="change.before ? 'rounded-b-[0.625rem]' : 'rounded-[0.625rem]'">
                  <div class="mb-1 text-xs font-semibold uppercase tracking-wide text-accent-strong dark:text-accent-soft">{{ t('landing.builder.jobMatch.after') }}</div>
                  <p class="whitespace-pre-line !text-sm text-ink dark:text-white">{{ change.after }}</p>
                </div>
              </template>
            </li>
          </ul>
        </div>

        <LandingQuickSignupDialog
          v-if="tailoredToken"
          v-model="showSignup"
          claim
          claim-kind="edit"
          :claim-token="tailoredToken"
          @signed-in="onSignedIn"
        />
        <InterviewPrepOffer
          v-if="tailoredToken" :source-token="tailoredToken" bare class="mt-6 border-t border-line pt-6 dark:border-gray-700"
          @buy-pack="emit('buy')" />
      </template>

      <!--
        One choice, cheapest first: this CV tailored as one PDF, then everything
        for this one application (the kit), then the pack for every posting, and
        the general CV stays free at the bottom, as promised.
      -->
      <div v-else-if="tiers && preview.limited && canBuyPack" id="cv-tailor-choice" class="scroll-mt-24">
        <div class="mb-3 font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tiers.title') }}</div>
        <div class="space-y-3">
          <div v-if="canBuyTailored" class="offer-option offer-option-main">
            <div class="flex items-baseline justify-between gap-3">
              <span class="font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tailored.singleTitle') }}</span>
              <span class="shrink-0 font-title text-2xl font-bold tabular-nums text-ink dark:text-white">{{ tailoredPdfPrice }}</span>
            </div>
            <p class="mt-1 !text-sm text-body dark:text-gray-300">{{ t('landing.builder.tailored.note') }}</p>
            <SofaxBtn
              variant="accent"
              block
              class="mt-3"
              :disabled="isBuying"
              :label="t('landing.builder.tailored.cta', { price: tailoredPdfPrice })"
              @click="emit('buy-tailored')"
            />
          </div>

          <div v-if="canBuyKit" class="offer-option">
            <LandingKitOption />
            <SofaxBtn
              variant="dark"
              block
              class="mt-3"
              :disabled="isBuying"
              :label="t('landing.builder.kit.cta', { price: applicationKitPrice })"
              @click="emit('buy-kit')"
            />
          </div>

          <div class="offer-option">
            <span class="tag mb-2">{{ t('landing.builder.tiers.best') }}</span>
            <div class="flex items-baseline justify-between gap-3">
              <span class="font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tiers.packTitle') }}</span>
              <span class="shrink-0 font-title text-2xl font-bold tabular-nums text-ink dark:text-white">{{ jobPackPrice }}</span>
            </div>
            <p class="mt-1 !text-sm text-body dark:text-gray-300">{{ t('landing.builder.tiers.packText', { three: threeApplications }) }}</p>
            <p v-if="creditNote" class="mt-1 !text-sm font-semibold text-ink dark:text-white">{{ creditNote }}</p>
            <SofaxBtn
              :variant="canBuyTailored ? 'outline' : 'accent'"
              block
              class="mt-3"
              :disabled="isBuying"
              :label="isBuying ? t('common.loading') : t('landing.builder.packOffer.cta', { price: jobPackPrice })"
              @click="emit('buy')"
            />
          </div>

          <div class="offer-option flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div class="min-w-0">
              <div class="font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tiers.freeTitle') }}</div>
              <p class="!text-sm text-muted dark:text-gray-400">{{ t('landing.builder.tiers.freeText') }}</p>
            </div>
            <button type="button" class="link shrink-0 !text-sm" :disabled="isExporting" @click="emit('download-free')">
              {{ t('landing.builder.tiers.freeCta') }}
            </button>
          </div>
        </div>
      </div>

      <!--
        Cheapest first: this one posting as one PDF (the product made for
        exactly what was just shown, so it leads), then everything for this one
        application (the kit), then an unlimited account for 30 days, with the
        sum that makes it the better deal for more than a couple of postings.
      -->
      <template v-else-if="preview.limited && canBuyTailored">
        <div class="mb-3 font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tailored.chooseTitle') }}</div>
        <div class="space-y-3">
          <div class="offer-option offer-option-main">
            <div class="flex items-baseline justify-between gap-3">
              <span class="font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tailored.singleTitle') }}</span>
              <span class="shrink-0 font-title text-2xl font-bold tabular-nums text-ink dark:text-white">{{ tailoredPdfPrice }}</span>
            </div>
            <p class="mt-1 !text-sm text-body dark:text-gray-300">{{ t('landing.builder.tailored.note') }}</p>
            <SofaxBtn
              variant="accent"
              block
              class="mt-3"
              :disabled="isBuying"
              :label="isBuying ? t('common.loading') : t('landing.builder.tailored.cta', { price: tailoredPdfPrice })"
              @click="emit('buy-tailored')"
            />
          </div>

          <div v-if="canBuyKit" class="offer-option">
            <LandingKitOption />
            <SofaxBtn
              variant="dark"
              block
              class="mt-3"
              :disabled="isBuying"
              :label="t('landing.builder.kit.cta', { price: applicationKitPrice })"
              @click="emit('buy-kit')"
            />
          </div>

          <div v-if="canBuyPack" class="offer-option">
            <div class="flex items-baseline justify-between gap-3">
              <span class="font-title text-base font-semibold text-ink dark:text-white">{{ t('landing.builder.tailored.unlimitedTitle') }}</span>
              <span class="shrink-0 font-title text-2xl font-bold tabular-nums text-ink dark:text-white">{{ jobPackPrice }}</span>
            </div>
            <p class="mt-1 !text-sm text-body dark:text-gray-300">
              {{ t('landing.builder.tailored.unlimitedText', { three: threeTailored }) }}
            </p>
            <p v-if="creditNote" class="mt-1 !text-sm font-semibold text-ink dark:text-white">{{ creditNote }}</p>
            <SofaxBtn
              variant="outline"
              block
              class="mt-3"
              :disabled="isBuying"
              :label="t('landing.builder.tailored.packCta', { price: jobPackPrice })"
              @click="emit('buy')"
            />
          </div>
        </div>
      </template>

      <template v-else-if="preview.limited && canBuyPack">
        <SofaxBtn
          variant="accent"
          block
          :disabled="isBuying"
          :label="isBuying ? t('common.loading') : t('landing.builder.jobMatch.matchCta', { price: jobPackPrice })"
          @click="emit('buy')"
        />
        <p class="mt-3 text-center !text-sm text-muted dark:text-gray-400">{{ t('landing.builder.jobMatch.matchCtaNote') }}</p>
      </template>
      <SofaxBtn
        v-else-if="!preview.limited"
        variant="accent"
        block
        :label="t('landing.builder.jobMatch.editCta')"
        @click="emit('edit')"
      />

      <p v-if="!tailoredPaid" class="mt-4 text-center">
        <button type="button" class="link !text-sm" @click="demo.reset()">{{ t('landing.builder.jobMatch.again') }}</button>
      </p>
    </template>
  </div>
</template>

<style scoped>
.offer-option {
  @apply rounded-[0.625rem] border border-line p-4 dark:border-gray-700;
}

.offer-option-main {
  @apply border-accent bg-accent/5 dark:bg-accent/10;
}

.score-bar {
  @apply flex items-center gap-3 rounded-[0.625rem] border border-line bg-white/95 px-3 py-2 shadow-lg backdrop-blur dark:border-gray-700 dark:bg-gray-900/95;
}

.score-bar-enter-active,
.score-bar-leave-active {
  @apply transition duration-200;
}

.score-bar-enter-from,
.score-bar-leave-to {
  @apply -translate-y-1 opacity-0;
}

/*
 * What a click does, on hover: only where there is a mouse to hover with. On a
 * phone the label on the button and the line above the list say the same.
 */
.hover-tip {
  @apply pointer-events-none absolute bottom-full left-0 z-20 mb-2 hidden w-max max-w-[16rem] whitespace-normal rounded-md bg-ink px-2.5 py-1.5 text-left text-xs font-normal leading-snug text-white shadow-lg dark:bg-white dark:text-ink;
}

.hover-tip-end {
  @apply left-auto right-0;
}

@media (hover: hover) {
  .group:hover > .hover-tip,
  .group:focus-visible > .hover-tip {
    display: block;
  }
}
</style>
